import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { api } from '../api';
import { mapCasinoApiGame, sideGameImageCandidates, warmGameIcon, filterImmersiveDeluxeByDevice } from '../data/casinoGames';
import { useIsMobile } from './useIsMobile';
import { mergeProviderOptions } from '../data/casinoData';
import { getActiveCurrency } from '../utils/currency';
import { AUTH_EVENT } from '../utils/authSync';

const feedCache = new Map();
const feedInflight = new Map();
const vendorsCache = new Map();
const vendorsInflight = new Map();
const MAX_CACHE = 64;
const MAX_VENDORS_CACHE = 24;
const SESSION_FEED_KEY = 'vb.casino.feed.v3';

function feedKey({ category, lobby, sort, provider, q, tagsKey, freespinOnly, pageSize, offset = 0, currency = null }) {
  const cur = String(currency || getActiveCurrency() || 'TRY').toUpperCase();
  return [cur, lobby || '', category || '', sort || '', provider || '', q || '', tagsKey || '', freespinOnly ? '1' : '0', pageSize, offset || 0].join('|');
}

function feedKeyPrefix({ category, lobby, sort, provider, q, tagsKey, freespinOnly, currency = null }) {
  const cur = String(currency || getActiveCurrency() || 'TRY').toUpperCase();
  return [cur, lobby || '', category || '', sort || '', provider || '', q || '', tagsKey || '', freespinOnly ? '1' : '0'].join('|');
}

export function clearCasinoFeedCaches() {
  feedCache.clear();
  feedInflight.clear();
  vendorsCache.clear();
  vendorsInflight.clear();
  try { sessionStorage.removeItem(SESSION_FEED_KEY); } catch { /* ignore */ }
}

function findCompatibleFeed(params) {
  const offset = params.offset || 0;
  const exactKey = feedKey({ ...params, offset });
  const exact = feedCache.get(exactKey) || (offset === 0 ? hydrateFeedFromSession(exactKey) : null);
  if (exact) return exact;
  if (offset !== 0) return null;

  const prefix = feedKeyPrefix(params);
  const need = Number(params.pageSize) || 0;
  let best = null;
  let bestSize = -1;

  for (const [key, value] of feedCache.entries()) {
    if (!value?.games?.length) continue;
    const parts = String(key).split('|');
    if (parts.length < 10) continue;
    if (parts.slice(0, 8).join('|') !== prefix) continue;
    if (Number(parts[9] || 0) !== 0) continue;
    const size = Number(parts[8]) || 0;
    if (size >= need && size > bestSize) {
      best = value;
      bestSize = size;
    }
  }

  if (!best) return null;
  if (best.games.length <= need) return best;
  return {
    games: best.games.slice(0, need),
    total: best.total,
  };
}

function readSessionFeeds() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(SESSION_FEED_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionFeed(key, value) {
  if (typeof window === 'undefined') return;
  try {
    const all = readSessionFeeds();
    all[key] = {
      games: value.games,
      total: value.total,
      ts: Date.now(),
    };
    const keys = Object.keys(all);
    if (keys.length > 24) {
      keys
        .sort((a, b) => (all[a]?.ts || 0) - (all[b]?.ts || 0))
        .slice(0, keys.length - 24)
        .forEach((k) => { delete all[k]; });
    }
    sessionStorage.setItem(SESSION_FEED_KEY, JSON.stringify(all));
  } catch {
  }
}

function hydrateFeedFromSession(key) {
  if (feedCache.has(key)) return feedCache.get(key);
  const all = readSessionFeeds();
  const hit = all[key];
  if (!hit?.games?.length) return null;
  if (Date.now() - (hit.ts || 0) > 10 * 60 * 1000) return null;
  const value = { games: hit.games, total: hit.total || hit.games.length };
  feedCache.set(key, value);
  return value;
}

function rememberFeed(key, value) {
  feedCache.set(key, value);
  writeSessionFeed(key, value);
  if (feedCache.size <= MAX_CACHE) return;
  const first = feedCache.keys().next().value;
  feedCache.delete(first);
}

export function prefetchGameIcons(games, limit = 14, size = 'md', { preferLandscape = false } = {}) {
  if (typeof window === 'undefined' || !Array.isArray(games) || !games.length) return;
  const list = games.slice(0, Math.max(0, limit));
  list.forEach((game) => {
    const src = sideGameImageCandidates(game, { size, preferLandscape })[0];
    if (!src) return;
    warmGameIcon(src);
  });
}

async function requestCasinoGames(params) {
  const key = feedKey({ ...params, offset: params.offset || 0 });
  const cached = findCompatibleFeed(params);
  if (cached) {
    if (!feedCache.has(key)) feedCache.set(key, cached);
    return cached;
  }

  const existing = feedInflight.get(key);
  if (existing) return existing;

  const query = {
    limit: params.pageSize,
    offset: params.offset || 0,
    sort: params.sort,
  };
  if (params.category) query.category = params.category;
  if (params.lobby && params.lobby !== 'all') query.lobby = params.lobby;
  if (params.provider) query.provider = params.provider;
  if (params.q?.trim()) query.q = params.q.trim();
  if (params.freespinOnly) query.freespin = '1';
  if (params.tagsKey) query.tags = params.tagsKey;

  const pending = api.casinoGames(query).then((result) => {
    const mapped = (result.games || []).map(mapCasinoApiGame);
    const value = {
      games: mapped,
      total: result.total ?? mapped.length,
    };
    rememberFeed(key, value);
    prefetchGameIcons(mapped, Math.min(12, mapped.length), 'sm');
    return value;
  }).finally(() => {
    feedInflight.delete(key);
  });

  feedInflight.set(key, pending);
  return pending;
}

export function prefetchCasinoGameFeed(options = {}) {
  const tagsKey = Array.isArray(options.tags) ? options.tags.join(',') : '';
  const params = {
    category: options.category || 'all',
    lobby: options.lobby || 'slots',
    sort: options.sort || 'popular',
    provider: options.provider || null,
    q: options.q || '',
    tagsKey,
    freespinOnly: Boolean(options.freespinOnly),
    pageSize: options.pageSize || 21,
    offset: 0,
  };
  const key = feedKey(params);
  const cached = findCompatibleFeed(params);
  if (cached) {
    if (!feedCache.has(key)) feedCache.set(key, cached);
    return Promise.resolve(cached);
  }
  if (feedInflight.has(key)) return feedInflight.get(key);
  return requestCasinoGames(params).catch(() => null);
}

function vendorsKey(lobby = 'slots') {
  const cur = getActiveCurrency() || 'TRY';
  const lobbyKey = lobby && lobby !== 'all' ? String(lobby) : '';
  return `${cur}|${lobbyKey}`;
}

function rememberVendors(key, value) {
  vendorsCache.set(key, value);
  if (vendorsCache.size <= MAX_VENDORS_CACHE) return;
  const first = vendorsCache.keys().next().value;
  vendorsCache.delete(first);
}

async function requestCasinoVendors(lobby = 'slots') {
  const key = vendorsKey(lobby);
  const cached = vendorsCache.get(key);
  if (cached) return cached;

  const existing = vendorsInflight.get(key);
  if (existing) return existing;

  const pending = api.casinoVendors(key ? { lobby: key } : {})
    .then((list) => {
      const value = mergeProviderOptions(Array.isArray(list) ? list : []);
      rememberVendors(key, value);
      return value;
    })
    .finally(() => {
      vendorsInflight.delete(key);
    });

  vendorsInflight.set(key, pending);
  return pending;
}

export function prefetchCasinoVendors(lobby = 'slots') {
  const key = vendorsKey(lobby);
  if (vendorsCache.has(key)) return Promise.resolve(vendorsCache.get(key));
  if (vendorsInflight.has(key)) return vendorsInflight.get(key);
  return requestCasinoVendors(lobby).catch(() => null);
}

export function useCasinoGameFeed({
  category = 'all',
  lobby = 'slots',
  sort = 'popular',
  provider = null,
  q = '',
  tags = [],
  freespinOnly = false,
  pageSize = 21,
  enabled = true,
} = {}) {
  const currency = getActiveCurrency() || 'TRY';
  const tagsKey = Array.isArray(tags) ? tags.join(',') : '';
  const baseParams = { category, lobby, sort, provider, q, tagsKey, freespinOnly, pageSize, currency };
  const key = feedKey({ ...baseParams, offset: 0 });
  const cached = findCompatibleFeed(baseParams);

  useEffect(() => {
    const onAuth = () => { clearCasinoFeedCaches(); };
    window.addEventListener(AUTH_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_EVENT, onAuth);
  }, []);

  const [games, setGames] = useState(() => cached?.games || []);
  const [total, setTotal] = useState(() => cached?.total || 0);
  const [loading, setLoading] = useState(() => enabled && !cached);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const gamesLenRef = useRef(cached?.games?.length || 0);
  const paramsRef = useRef(baseParams);

  gamesLenRef.current = games.length;
  paramsRef.current = baseParams;

  const fetchPage = useCallback(async (offset, append) => {
    const requestId = ++requestIdRef.current;
    const params = paramsRef.current;
    const cacheKey = feedKey({ ...params, offset: append ? offset : 0 });

    if (!append) {
      const hit = findCompatibleFeed({ ...params, offset: 0 });
      if (hit) {
        if (!feedCache.has(cacheKey)) feedCache.set(cacheKey, hit);
        setGames(hit.games);
        gamesLenRef.current = hit.games.length;
        setTotal(hit.total);
        setLoading(false);
        setRefreshing(false);
        setError(null);
        return;
      }
      if (gamesLenRef.current === 0) {
        setLoading(true);
        setRefreshing(false);
      } else {
        setRefreshing(true);
      }
    } else {
      const pageHit = feedCache.get(cacheKey) || hydrateFeedFromSession(cacheKey);
      if (pageHit?.games?.length) {
        setGames((prev) => {
          const seen = new Set(prev.map((g) => g.id));
          const merged = [...prev];
          pageHit.games.forEach((g) => {
            if (!seen.has(g.id)) merged.push(g);
          });
          gamesLenRef.current = merged.length;
          rememberFeed(feedKey({ ...params, offset: 0 }), { games: merged, total: pageHit.total || merged.length });
          return merged;
        });
        setTotal((prev) => pageHit.total || prev);
        setLoadingMore(false);
        setError(null);
        return;
      }
      setLoadingMore(true);
    }

    try {
      if (append) {
        const value = await requestCasinoGames({ ...params, offset });
        if (requestId !== requestIdRef.current) return;
        setGames((prev) => {
          const seen = new Set(prev.map((g) => g.id));
          const next = [...prev];
          value.games.forEach((g) => {
            if (!seen.has(g.id)) next.push(g);
          });
          gamesLenRef.current = next.length;
          rememberFeed(feedKey({ ...params, offset: 0 }), { games: next, total: value.total });
          return next;
        });
        setTotal(value.total);
        setError(null);
        prefetchGameIcons(value.games, Math.min(12, value.games.length), 'sm');
      } else {
        const value = await requestCasinoGames({ ...params, offset: 0 });
        if (requestId !== requestIdRef.current) return;
        setGames(value.games);
        gamesLenRef.current = value.games.length;
        setTotal(value.total);
        setError(null);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err);
      if (!append && gamesLenRef.current === 0) {
        setGames([]);
        setTotal(0);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    const hit = feedCache.get(key) || hydrateFeedFromSession(key);
    if (hit) {
      setGames(hit.games);
      gamesLenRef.current = hit.games.length;
      setTotal(hit.total);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      return;
    }
    setGames([]);
    gamesLenRef.current = 0;
    setTotal(0);
    setLoading(true);
    setRefreshing(false);
    setError(null);
    fetchPage(0, false);
  }, [enabled, key, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || refreshing || games.length >= total) return;
    fetchPage(games.length, true);
  }, [loadingMore, loading, refreshing, games.length, total, fetchPage]);

  const refresh = useCallback(() => {
    feedCache.delete(key);
    try {
      const all = readSessionFeeds();
      delete all[key];
      sessionStorage.setItem(SESSION_FEED_KEY, JSON.stringify(all));
    } catch {
    }
    return fetchPage(0, false);
  }, [fetchPage, key]);

  const isMobile = useIsMobile();
  const visibleGames = useMemo(
    () => filterImmersiveDeluxeByDevice(games, isMobile),
    [games, isMobile],
  );

  return {
    games: visibleGames,
    total,
    loading,
    refreshing,
    loadingMore,
    error,
    hasMore: games.length < total,
    loadMore,
    refresh,
  };
}

export function useCasinoVendors({ lobby = 'slots', enabled = true } = {}) {
  const key = vendorsKey(lobby);
  const cached = vendorsCache.get(key);
  const [vendors, setVendors] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => enabled && !cached);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    const hit = vendorsCache.get(key);
    if (hit) {
      setVendors(hit);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    requestCasinoVendors(lobby)
      .then((list) => {
        if (alive) setVendors(list);
      })
      .catch(() => {
        if (alive) setVendors([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [lobby, enabled, key]);

  return { vendors, loading };
}

export function useCasinoStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.casinoStats()
      .then((data) => {
        if (alive) setStats(data);
      })
      .catch(() => {
        if (alive) setStats(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { stats, loading };
}
